import React, { useCallback, useMemo, useState } from "react";
import { Redirect, useHistory, useParams } from "react-router-dom";

import { JollyRancherChip } from "veneer/Chip";
import DestructiveConfirmationDialog from "veneer/DestructiveConfirmationDialog";
import DropdownMenu from "veneer/DropdownMenu";
import MenuItem from "veneer/MenuItem";
import Stack from "veneer/Stack";
import {
  AppSettingsIcon,
  EditIcon,
  FlagIcon,
  SettingsIcon,
  TrashIcon,
} from "veneer/SvgIcon";
import Typography from "veneer/Typography";

import { upgradablePlayerVersionsSelector } from "cortex/ui/src/fleet/redux-store/ui-state/player-versions/selectors";
import type { Device } from "cortex/ui/src/fleet/view/device/model";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { Network } from "cortex/ui/src/network/model";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas";
import { getAuthUser } from "cortex/ui/src/redux-store/auth/selectors";
import { deviceWithStatusSelector } from "cortex/ui/src/redux-store/derivativeSelectors";
import { getDeviceVersionMetricsForSelector } from "cortex/ui/src/redux-store/devices/selectors";
import {
  calculateLastSeenDeviceCounts,
  devicesLastSeenSelector,
} from "cortex/ui/src/redux-store/last-seen-devices/selectors";
import { isSandbox, isSystemManaged } from "cortex/ui/src/redux-store/networks";
import { DELETE_NETWORK } from "cortex/ui/src/redux-store/networks/actions";
import {
  networksSelector,
  networksStoresLoadedSelector,
} from "cortex/ui/src/redux-store/networks/selectors";
import { isReadonly } from "cortex/ui/src/redux-store/users/selectors";
import FullDeviceListTable from "cortex/ui/src/ui/full-device-list/FullDeviceListTable";
import { NavLoader } from "cortex/ui/src/ui/loader";
import Notification from "cortex/ui/src/ui/notification";
import WsDsConnStatus from "cortex/ui/src/ui/ws-ds-conn-status/WsDsConnStatus";
import { User } from "cortex/ui/src/user/model";
import { getBreadcrumbs } from "cortex/ui/src/util/breadcrumbs";
import { getErrorMessage } from "cortex/ui/src/util/response";
import { Container } from "cortex/ui/src/view/layout";

interface NetworkViewPageProps {
  deleteNetwork: (networkId: number | string) => Promise<void>;
  deviceStatsCounts?: ReturnType<typeof calculateLastSeenDeviceCounts>;
  deviceMetricsCounts?: {
    upgradeAvailable: number;
    pendingUpgrade: number;
    pendingDowngrade: number;
    pendingVersionChange: number;
    neverConnected: number;
  };
  navigate: (url: string) => void;
  network?: Network;
  storesLoaded: boolean;
  user?: User | null;
}

type NetworkDeviceMetricsCounts = NonNullable<
  NetworkViewPageProps["deviceMetricsCounts"]
>;

export function NetworkViewPage({
  deleteNetwork,
  deviceStatsCounts,
  deviceMetricsCounts,
  navigate,
  network,
  storesLoaded,
  user,
}: NetworkViewPageProps) {
  const [deleting, setDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const showDeleteModal = useCallback(() => {
    setShowModal(true);
  }, []);

  const hideModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleDeleteNetwork = useCallback(() => {
    const networkId = network?.id;

    if (!networkId) {
      return;
    }

    return deleteNetwork(networkId).catch((error) => {
      setDeleting(false);
      setErrorMessage(getErrorMessage(error));
      setSuccessMessage("");
    });
  }, [deleteNetwork, network]);

  const submit = useCallback(() => {
    setDeleting(true);
    handleDeleteNetwork();
    hideModal();

    return Promise.resolve();
  }, [handleDeleteNetwork, hideModal]);

  const renderBreadcrumbs = useCallback(() => {
    return getBreadcrumbs([{ label: "Networks", target: "/networks" }]);
  }, []);

  const renderToolbox = () => {
    if (!network) {
      return null;
    }

    const readonly = isReadonly(user);
    const parameterTitle = readonly ? "View Parameters" : "Edit Parameters";
    const flagsTitle = readonly ? "View Flags" : "Edit Flags";
    const networkId = network.id;

    return (
      <DropdownMenu
        dataTestId="network-view-configure-menu"
        icon={<SettingsIcon size="small" />}
        label="Configure"
        variant="contained"
      >
        {!readonly && (
          <MenuItem
            dataTestId="network-view-edit-network"
            onClick={() => navigate(`/networks/${networkId}/edit`)}
          >
            <Stack alignItems="center" direction="row" spacing={4}>
              <EditIcon />
              <Typography>Edit Network</Typography>
            </Stack>
          </MenuItem>
        )}
        <MenuItem
          dataTestId="network-view-parameters"
          onClick={() => navigate(`/networks/${networkId}/configure`)}
        >
          <Stack alignItems="center" direction="row" spacing={4}>
            <AppSettingsIcon />
            <Typography>{parameterTitle}</Typography>
          </Stack>
        </MenuItem>
        <MenuItem
          dataTestId="network-view-flags"
          onClick={() => navigate(`/networks/${networkId}/flags`)}
        >
          <Stack alignItems="center" direction="row" spacing={4}>
            <FlagIcon />
            <Typography>{flagsTitle}</Typography>
          </Stack>
        </MenuItem>
        {!readonly && (
          <MenuItem
            dataTestId="network-view-delete-network"
            onClick={showDeleteModal}
          >
            <Stack alignItems="center" direction="row" spacing={4}>
              <TrashIcon />
              <Typography>Delete Network</Typography>
            </Stack>
          </MenuItem>
        )}
      </DropdownMenu>
    );
  };

  const renderDesc = () => {
    if (!network) {
      return "";
    }

    return (
      <Stack alignItems="center" direction="row" spacing={1}>
        {isSandbox(network) ? (
          <JollyRancherChip color="gray" label="Sandbox Network" />
        ) : (
          <JollyRancherChip color="green" label="Production Network" />
        )}
        {isSystemManaged(network) && (
          <JollyRancherChip color="red" label="Ad Platform Synced" />
        )}
      </Stack>
    );
  };

  if (deleting && !network) {
    return <Redirect push to="/networks" />;
  }

  if (!storesLoaded) {
    return (
      <Container breadcrumbs={renderBreadcrumbs()} hideBox>
        <NavLoader.Proxy />
      </Container>
    );
  }

  if (!deleting && !network) {
    return <Redirect to="/404" />;
  }

  return (
    <Container
      breadcrumbs={renderBreadcrumbs()}
      desc={renderDesc()}
      hideBox
      title={network ? network.name : ""}
      toolbox={renderToolbox()}
    >
      <Notification error={errorMessage} success={successMessage} />
      <WsDsConnStatus />
      <FullDeviceListTable
        deviceMetricsCounts={deviceMetricsCounts}
        deviceStatsCounts={deviceStatsCounts}
        networkFilter={false}
        prefiltered={{ network: network?.id }}
        storageKey={`network_devices_acct_${user?.account_id ?? 0}`}
      />
      {showModal && (
        <DestructiveConfirmationDialog
          commitLabel="Delete"
          dataTestId={{
            component: "network-view-delete-dialog",
            main: "network-view-delete-confirm",
            secondary: "network-view-delete-cancel",
          }}
          onCancel={hideModal}
          onCommit={submit}
          open={showModal}
          title="Are you sure?"
          variant="modal"
        >
          This Network will be deleted permanently.
        </DestructiveConfirmationDialog>
      )}
    </Container>
  );
}

export default function NetworkView() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const networks = useAppSelector(networksSelector);
  const network = networks[id] as Network | undefined;
  const devicesById = useAppSelector(deviceWithStatusSelector);
  const devicesLastSeenById = useAppSelector(devicesLastSeenSelector);
  const upgradablePlayerVersions = useAppSelector(
    upgradablePlayerVersionsSelector,
  );
  const storesLoaded = useAppSelector(networksStoresLoadedSelector);
  const user = useAppSelector(getAuthUser);
  const deviceStatsCounts = useMemo(() => {
    if (!network) {
      return undefined;
    }
    return calculateLastSeenDeviceCounts(
      devicesById,
      devicesLastSeenById,
      network.id,
      networks,
    );
  }, [devicesById, devicesLastSeenById, network?.id, networks]);
  const deviceMetricsCounts = useMemo<
    NetworkViewPageProps["deviceMetricsCounts"]
  >(() => {
    if (!network) {
      return undefined;
    }

    return Object.values(devicesById)
      .filter((device) => Number(device.network_id) === Number(network.id))
      .reduce<NetworkDeviceMetricsCounts>(
        (totals, device) => {
          const metrics = getDeviceVersionMetricsForSelector(
            device as Device,
            Array.from(upgradablePlayerVersions),
          );

          if (metrics.upgradeAvailable) {
            totals.upgradeAvailable += 1;
          }

          if (metrics.pendingUpgrade) {
            totals.pendingUpgrade += 1;
          }

          if (metrics.pendingDowngrade) {
            totals.pendingDowngrade += 1;
          }

          if (metrics.pendingVersionChange) {
            totals.pendingVersionChange += 1;
          }

          if (metrics.neverConnected) {
            totals.neverConnected += 1;
          }

          return totals;
        },
        {
          neverConnected: 0,
          pendingDowngrade: 0,
          pendingUpgrade: 0,
          pendingVersionChange: 0,
          upgradeAvailable: 0,
        },
      );
  }, [devicesById, network, upgradablePlayerVersions]);

  return (
    <NetworkViewPage
      deleteNetwork={(networkId) => {
        return createPromiseApiAction(dispatch, DELETE_NETWORK, [
          networkId,
        ]).then(() => undefined);
      }}
      deviceMetricsCounts={deviceMetricsCounts}
      deviceStatsCounts={deviceStatsCounts}
      navigate={history.push}
      network={network}
      storesLoaded={storesLoaded}
      user={user}
    />
  );
}
